/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import {
  InfiniteData,
  InvalidateQueryFilters,
  QueryClient,
  QueryKey,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useQuery,
  UseQueryResult,
  useSuspenseInfiniteQuery,
  UseSuspenseInfiniteQueryResult,
  useSuspenseQuery,
  UseSuspenseQueryResult,
} from "@tanstack/react-query";
import { GramError } from "../models/errors/gramerror.js";
import {
  ConnectionError,
  InvalidRequestError,
  RequestAbortedError,
  RequestTimeoutError,
  UnexpectedClientError,
} from "../models/errors/httpclienterrors.js";
import { ResponseValidationError } from "../models/errors/responsevalidationerror.js";
import { SDKValidationError } from "../models/errors/sdkvalidationerror.js";
import { ServiceError } from "../models/errors/serviceerror.js";
import {
  Classifications,
  ListSkillsQueryParamSort,
  ListSkillsRequest,
  ListSkillsSecurity,
  SourceKinds,
} from "../models/operations/listskills.js";
import { useGramContext } from "./_context.js";
import {
  InfiniteQueryHookOptions,
  QueryHookOptions,
  SuspenseInfiniteQueryHookOptions,
  SuspenseQueryHookOptions,
  TupleToPrefixes,
} from "./_types.js";
import {
  buildSkillsInfiniteQuery,
  buildSkillsQuery,
  prefetchSkills,
  prefetchSkillsInfinite,
  queryKeySkills,
  queryKeySkillsInfinite,
  SkillsInfiniteQueryData,
  SkillsPageParams,
  SkillsQueryData,
} from "./skills.core.js";
export {
  buildSkillsInfiniteQuery,
  buildSkillsQuery,
  prefetchSkills,
  prefetchSkillsInfinite,
  queryKeySkills,
  queryKeySkillsInfinite,
  type SkillsInfiniteQueryData,
  type SkillsPageParams,
  type SkillsQueryData,
};

export type SkillsQueryError =
  | ServiceError
  | GramError
  | ResponseValidationError
  | ConnectionError
  | RequestAbortedError
  | RequestTimeoutError
  | InvalidRequestError
  | UnexpectedClientError
  | SDKValidationError;

/**
 * list skills
 *
 * @remarks
 * List active skills in the project. The implementation requires the skills product feature and skill read scope.
 */
export function useSkills(
  request?: ListSkillsRequest | undefined,
  security?: ListSkillsSecurity | undefined,
  options?: QueryHookOptions<SkillsQueryData, SkillsQueryError>,
): UseQueryResult<SkillsQueryData, SkillsQueryError> {
  const client = useGramContext();
  return useQuery({
    ...buildSkillsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * list skills
 *
 * @remarks
 * List active skills in the project. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillsSuspense(
  request?: ListSkillsRequest | undefined,
  security?: ListSkillsSecurity | undefined,
  options?: SuspenseQueryHookOptions<SkillsQueryData, SkillsQueryError>,
): UseSuspenseQueryResult<SkillsQueryData, SkillsQueryError> {
  const client = useGramContext();
  return useSuspenseQuery({
    ...buildSkillsQuery(
      client,
      request,
      security,
      options,
    ),
    ...options,
  });
}

/**
 * list skills
 *
 * @remarks
 * List active skills in the project. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillsInfinite(
  request?: ListSkillsRequest | undefined,
  security?: ListSkillsSecurity | undefined,
  options?: InfiniteQueryHookOptions<SkillsInfiniteQueryData, SkillsQueryError>,
): UseInfiniteQueryResult<
  InfiniteData<SkillsInfiniteQueryData, SkillsPageParams>,
  SkillsQueryError
> {
  const client = useGramContext();
  return useInfiniteQuery<
    SkillsInfiniteQueryData,
    SkillsQueryError,
    InfiniteData<SkillsInfiniteQueryData, SkillsPageParams>,
    QueryKey,
    SkillsPageParams
  >({
    ...buildSkillsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

/**
 * list skills
 *
 * @remarks
 * List active skills in the project. The implementation requires the skills product feature and skill read scope.
 */
export function useSkillsInfiniteSuspense(
  request?: ListSkillsRequest | undefined,
  security?: ListSkillsSecurity | undefined,
  options?: SuspenseInfiniteQueryHookOptions<
    SkillsInfiniteQueryData,
    SkillsQueryError
  >,
): UseSuspenseInfiniteQueryResult<
  InfiniteData<SkillsInfiniteQueryData, SkillsPageParams>,
  SkillsQueryError
> {
  const client = useGramContext();
  return useSuspenseInfiniteQuery<
    SkillsInfiniteQueryData,
    SkillsQueryError,
    InfiniteData<SkillsInfiniteQueryData, SkillsPageParams>,
    QueryKey,
    SkillsPageParams
  >({
    ...buildSkillsInfiniteQuery(
      client,
      request,
      security,
      options,
    ),
    initialPageParam: options?.initialPageParam,
    getNextPageParam: (previousPage) => previousPage["~next"],
    ...options,
  });
}

export function setSkillsData(
  client: QueryClient,
  queryKeyBase: [
    parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      search?: string | undefined;
      sourceKinds?: Array<SourceKinds> | undefined;
      classifications?: Array<Classifications> | undefined;
      tags?: Array<string> | undefined;
      accessibleBy?: Array<string> | undefined;
      sort?: ListSkillsQueryParamSort | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    },
  ],
  data: SkillsQueryData,
): SkillsQueryData | undefined {
  const key = queryKeySkills(...queryKeyBase);

  return client.setQueryData<SkillsQueryData>(key, data);
}

export function invalidateSkills(
  client: QueryClient,
  queryKeyBase: TupleToPrefixes<
    [parameters: {
      cursor?: string | undefined;
      limit?: number | undefined;
      search?: string | undefined;
      sourceKinds?: Array<SourceKinds> | undefined;
      classifications?: Array<Classifications> | undefined;
      tags?: Array<string> | undefined;
      accessibleBy?: Array<string> | undefined;
      sort?: ListSkillsQueryParamSort | undefined;
      gramSession?: string | undefined;
      gramKey?: string | undefined;
      gramProject?: string | undefined;
    }]
  >,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "list", ...queryKeyBase],
  });
}

export function invalidateAllSkills(
  client: QueryClient,
  filters?: Omit<InvalidateQueryFilters, "queryKey" | "predicate" | "exact">,
): Promise<void> {
  return client.invalidateQueries({
    ...filters,
    queryKey: ["@gram/client", "skills", "list"],
  });
}
